import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Chip,
  Popover,
  Menu,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  FormControlLabel,
} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LabelIcon from '@mui/icons-material/Label';
import RepeatIcon from '@mui/icons-material/Repeat';
import * as MuiIcons from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';

const WEEKLY_DAYS = [
  { value: 'monday', label: 'Mon' },
  { value: 'tuesday', label: 'Tue' },
  { value: 'wednesday', label: 'Wed' },
  { value: 'thursday', label: 'Thu' },
  { value: 'friday', label: 'Fri' },
  { value: 'saturday', label: 'Sat' },
  { value: 'sunday', label: 'Sun' },
];

const defaultRecurring = {
  enabled: false,
  frequency: 'daily',
  customValue: 1,
  customUnit: 'days',
  weeklyDays: [],
};

function QuickAddForm({ categories, selectedCategory, onQuickAdd }) {
  const theme = useTheme();
  const [title, setTitle] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [category, setCategory] = useState('');
  const [recurring, setRecurring] = useState(defaultRecurring);
  const [loading, setLoading] = useState(false);

  const [dateAnchor, setDateAnchor] = useState(null);
  const [categoryAnchor, setCategoryAnchor] = useState(null);
  const [recurringModalOpen, setRecurringModalOpen] = useState(false);
  const dateInputRef = React.useRef(null);

  useEffect(() => {
    setCategory((prev) => (selectedCategory ? selectedCategory : prev || ''));
  }, [selectedCategory]);

  const clearForm = () => {
    setTitle('');
    setDueDate('');
    setCategory(selectedCategory || '');
    setRecurring({ ...defaultRecurring });
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const trimmedTitle = (title || '').trim();
    if (!trimmedTitle) return;

    if (recurring.enabled && recurring.frequency === 'weekly') {
      if (!recurring.weeklyDays || recurring.weeklyDays.length === 0) {
        alert('Please select at least one day for weekly recurrence');
        return;
      }
    }

    const categoryValue = selectedCategory || category || null;
    const payload = {
      title: trimmedTitle,
      description: '',
      priority: 'medium',
      category: categoryValue,
      dueDate: dueDate || undefined,
      recurring: {
        enabled: recurring.enabled,
        frequency: recurring.frequency,
        customValue: recurring.customValue ?? 1,
        customUnit: recurring.customUnit ?? 'days',
        weeklyDays: recurring.weeklyDays || [],
      },
    };

    setLoading(true);
    try {
      await onQuickAdd(payload);
      clearForm();
    } catch (err) {
      // Error handled by parent
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const toggleWeeklyDay = (dayValue) => {
    const current = recurring.weeklyDays || [];
    const next = current.includes(dayValue)
      ? current.filter((d) => d !== dayValue)
      : [...current, dayValue];
    setRecurring((prev) => ({ ...prev, weeklyDays: next }));
  };

  const selectedCategoryObj = category ? categories.find((c) => c._id === category) : null;
  const CategoryDisplayIcon = selectedCategoryObj && MuiIcons[selectedCategoryObj.icon]
    ? MuiIcons[selectedCategoryObj.icon]
    : LabelIcon;

  const selectProps = {
    sx: {
      borderRadius: 2,
      bgcolor: theme.palette.background.paper,
      color: theme.palette.text.primary,
      '.MuiSelect-icon': { color: theme.palette.text.secondary },
    },
    MenuProps: {
      PaperProps: {
        sx: {
          bgcolor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          '& .MuiMenuItem-root': { color: theme.palette.text.primary },
          '& .MuiMenuItem-root.Mui-selected': { bgcolor: theme.palette.action.selected },
        },
      },
    },
  };

  const isDark = theme.palette.mode === 'dark';
  const barShadow = isDark ? '0px 1px 4px rgba(0, 0, 0, 0.2)' : '0px 1px 4px rgba(0, 0, 0, 0.04)';
  const barShadowHover = isDark ? '0px 2px 8px rgba(0, 0, 0, 0.25)' : '0px 2px 8px rgba(0, 0, 0, 0.06)';
  const surfaceShadow = isDark ? '0px 2px 12px rgba(0, 0, 0, 0.3)' : '0px 2px 12px rgba(0, 0, 0, 0.08)';

  const iconButtonSx = (isActive) => ({
    color: isActive ? theme.palette.primary.main : theme.palette.text.secondary,
    borderRadius: '12px',
    transition: 'all 80ms ease-out',
    '&:hover': {
      bgcolor: theme.palette.action.hover,
      boxShadow: barShadowHover,
      transform: 'translateY(-1px)',
    },
    ...(isActive && {
      bgcolor: alpha(theme.palette.primary.main, 0.12),
    }),
  });

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mb: 2.5 }}>
      {/* Unified card-style bar matching task list items */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 0.5,
          px: 3,
          py: 1.5,
          borderRadius: '14px',
          bgcolor: theme.palette.background.paper,
          border: '1.5px solid',
          borderColor: theme.palette.divider,
          boxShadow: barShadow,
          transition: 'all 80ms ease-out',
          '&:focus-within': {
            boxShadow: barShadowHover,
            borderColor: theme.palette.divider,
          },
        }}
      >
        <TextField
          placeholder="Add a task quickly..."
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={loading}
          size="small"
          sx={{
            flex: '1 1 200px',
            minWidth: 180,
            '& .MuiOutlinedInput-root': {
              bgcolor: 'transparent',
              '& fieldset': { border: 'none' },
              '&:hover fieldset': { border: 'none' },
              '&.Mui-focused fieldset': { border: 'none' },
            },
            '& .MuiInputBase-input': {
              fontSize: '1rem',
              fontWeight: 400,
              py: 1.25,
            },
          }}
          inputProps={{ 'aria-label': 'Quick add task title' }}
        />

        {/* Due date: calendar icon only, click opens date picker */}
        <IconButton
          size="small"
          onClick={(e) => setDateAnchor(e.currentTarget)}
          disabled={loading}
          sx={iconButtonSx(!!dueDate)}
          aria-label="Due date"
        >
          <CalendarTodayIcon fontSize="small" />
        </IconButton>
        <Popover
          open={Boolean(dateAnchor)}
          anchorEl={dateAnchor}
          onClose={() => setDateAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          TransitionProps={{
            onEntered: () => {
              if (typeof dateInputRef.current?.showPicker === 'function') {
                dateInputRef.current.showPicker();
              }
            },
          }}
          PaperProps={{
            sx: {
              borderRadius: '12px',
              p: 2,
              bgcolor: theme.palette.background.paper,
              border: '1px solid',
              borderColor: theme.palette.divider,
              boxShadow: surfaceShadow,
            },
          }}
        >
          <TextField
            inputRef={dateInputRef}
            type="date"
            value={dueDate}
            onChange={(e) => {
              setDueDate(e.target.value);
              setDateAnchor(null);
            }}
            size="small"
            InputLabelProps={{ shrink: true }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: theme.palette.background.paper,
              },
            }}
          />
        </Popover>

        {/* Category: icon only (All or selected category), click opens menu */}
        {!selectedCategory && (
          <>
            <IconButton
              size="small"
              onClick={(e) => setCategoryAnchor(e.currentTarget)}
              disabled={loading}
              sx={iconButtonSx(!!category)}
              aria-label="Category"
            >
              <CategoryDisplayIcon fontSize="small" />
            </IconButton>
            <Menu
              anchorEl={categoryAnchor}
              open={Boolean(categoryAnchor)}
              onClose={() => setCategoryAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              PaperProps={{
                sx: {
                  borderRadius: '12px',
                  minWidth: 160,
                  bgcolor: theme.palette.background.paper,
                  border: '1px solid',
                  borderColor: theme.palette.divider,
                  boxShadow: surfaceShadow,
                  '& .MuiMenuItem-root': { color: theme.palette.text.primary },
                },
              }}
            >
              <MenuItem
                selected={!category}
                onClick={() => {
                  setCategory('');
                  setCategoryAnchor(null);
                }}
              >
                <LabelIcon fontSize="small" sx={{ mr: 1.5, opacity: 0.8 }} />
                All
              </MenuItem>
              {categories.map((cat) => {
                const Icon = MuiIcons[cat.icon] || LabelIcon;
                return (
                  <MenuItem
                    key={cat._id}
                    selected={category === cat._id}
                    onClick={() => {
                      setCategory(cat._id);
                      setCategoryAnchor(null);
                    }}
                  >
                    {Icon && <Icon fontSize="small" sx={{ mr: 1.5, opacity: 0.8 }} />}
                    {cat.name}
                  </MenuItem>
                );
              })}
            </Menu>
          </>
        )}

        {/* Recurring: icon only, click opens modal */}
        <IconButton
          size="small"
          onClick={() => setRecurringModalOpen(true)}
          disabled={loading}
          sx={iconButtonSx(recurring.enabled)}
          aria-label="Recurring"
        >
          <RepeatIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Recurring settings modal */}
      <Dialog
        open={recurringModalOpen}
        onClose={() => setRecurringModalOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '14px',
            bgcolor: theme.palette.background.paper,
            border: '1px solid',
            borderColor: theme.palette.divider,
            boxShadow: surfaceShadow,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Recurring</DialogTitle>
        <DialogContent>
          <FormControlLabel
            control={
              <Switch
                checked={recurring.enabled}
                onChange={(e) =>
                  setRecurring((prev) => ({ ...prev, enabled: e.target.checked }))
                }
              />
            }
            label="Make this task recurring"
            sx={{ color: theme.palette.text.primary, mb: 2 }}
          />
          {recurring.enabled && (
            <Box sx={{ mt: 2 }}>
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel sx={{ color: theme.palette.text.primary }}>Repeat</InputLabel>
                <Select
                  value={recurring.frequency}
                  onChange={(e) =>
                    setRecurring((prev) => ({ ...prev, frequency: e.target.value }))
                  }
                  label="Repeat"
                  {...selectProps}
                >
                  <MenuItem value="daily">Daily</MenuItem>
                  <MenuItem value="weekly">Weekly</MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem>
                  <MenuItem value="yearly">Yearly</MenuItem>
                  <MenuItem value="custom">Custom</MenuItem>
                </Select>
              </FormControl>

              {recurring.frequency === 'weekly' && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: theme.palette.text.secondary }}>
                    Select days
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {WEEKLY_DAYS.map(({ value, label }) => {
                      const isSelected = (recurring.weeklyDays || []).includes(value);
                      return (
                        <Chip
                          key={value}
                          label={label}
                          size="small"
                          clickable
                          color={isSelected ? 'primary' : 'default'}
                          variant={isSelected ? 'filled' : 'outlined'}
                          onClick={() => toggleWeeklyDay(value)}
                          sx={{ minWidth: 44 }}
                        />
                      );
                    })}
                  </Box>
                </Box>
              )}

              {recurring.frequency === 'custom' && (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Value"
                      type="number"
                      size="small"
                      value={recurring.customValue}
                      onChange={(e) =>
                        setRecurring((prev) => ({
                          ...prev,
                          customValue: parseInt(e.target.value, 10) || 1,
                        }))
                      }
                      inputProps={{ min: 1 }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Unit</InputLabel>
                      <Select
                        value={recurring.customUnit}
                        onChange={(e) =>
                          setRecurring((prev) => ({ ...prev, customUnit: e.target.value }))
                        }
                        label="Unit"
                        {...selectProps}
                      >
                        <MenuItem value="days">Days</MenuItem>
                        <MenuItem value="weeks">Weeks</MenuItem>
                        <MenuItem value="months">Months</MenuItem>
                        <MenuItem value="years">Years</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRecurringModalOpen(false)} color="primary">
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default QuickAddForm;
